/* eslint-disable unicorn/prefer-at */
/* eslint-disable unicorn/consistent-function-scoping */
/* eslint-disable lingui/no-unlocalized-strings */
import { UrlDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";

export const printResume = async (data: { id: string }) => {
  const response = await axios.get<UrlDto>(`/resume/print/${data.id}`);

  return response.data;
};

export const usePrintResume = () => {
  const showSequentialToast = async (options: {
    title: string;
    description: string;
    duration?: number;
    delay?: number;
  }) => {
    const { delay = 800 } = options;

    toast({
      variant: "default",
      title: options.title,
      description: options.description,
      duration: 15_000, // 中间过程的提示持续时间较长
    });

    await new Promise((resolve) => setTimeout(resolve, delay));
  };

  const {
    error,
    isPending: loading,
    mutateAsync: printResumeFn,
  } = useMutation({
    mutationFn: async (data: { id: string }) => {
      await showSequentialToast({
        title: "🚀 启动简历生成引擎",
        description: "正在初始化核心组件...",
      });

      await showSequentialToast({
        title: "⚙️ 系统预热完成",
        description: "正在调用渲染模块...",
      });

      const response = await printResume(data);

      await showSequentialToast({
        title: "🔄 模板解析完成",
        description: "正在进行数据整合...",
      });

      return response;
    },
    onSuccess: async (data) => {
      try {
        await showSequentialToast({
          title: "⚡ 正在进行量子压缩",
          description: "使用先进算法优化文件大小...",
        });

        const response = await fetch(data.url);
        if (!response.ok) {
          throw new Error("PDF获取失败");
        }

        const blob = await response.blob();
        const fileSizeInMB = blob.size / (1024 * 1024);

        await showSequentialToast({
          title: "📊 文件分析完成",
          description: `文档大小: ${fileSizeInMB.toFixed(2)}MB | 正在准备传输通道...`,
        });

        await showSequentialToast({
          title: "📡 建立安全连接",
          description: "正在初始化本地传输协议...",
          delay: 600,
        });

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const urlParts = data.url.split("/");
        const filename = urlParts[urlParts.length - 1]; // 获取 URL 最后一部分作为文件名
        link.download = filename; // 直接使用服务端生成的文件名

        document.body.append(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        // 最终成功提示，设置较短的持续时间
        toast({
          variant: "success",
          title: "✨ 传输任务完成",
          description: "简历文件已经成功投放到您的本地终端",
          duration: 3000, // 3秒后自动消失
        });
      } catch (error) {
        console.error("PDF下载错误:", error);
        toast({
          variant: "error",
          title: "⚠️ 系统异常",
          description: `量子通道发生波动，传输失败。错误代码: ${
            error instanceof Error ? error.message : "UNKNOWN_ERROR"
          }`,
          duration: 5000,
        });
      }
    },
    onError: (error) => {
      console.error("服务器错误:", error);
      toast({
        variant: "error",
        title: "🚨 服务器响应异常",
        description: `核心服务器无响应，错误代码: ${error.message}`,
        duration: 5000,
      });
    },
  });

  return { printResume: printResumeFn, loading, error };
};
